import { DateTime } from 'luxon'
import { ChartAggregation } from 'models'

import { CsvCell } from './csv'

const MAX_BUCKETS = 200_000

export type TimelineExportPoint = {
  timestamp: DateTime
  values: Record<string, number | null | undefined>
}

export type TimelineCsvInput = {
  columns: string[]
  points: TimelineExportPoint[]
  granularity: ChartAggregation
  timeZone: string
  from?: DateTime
  to?: DateTime
  now?: DateTime
}

function extreme(points: TimelineExportPoint[], pick: (a: number, b: number) => number) {
  return points.reduce((acc, point) => pick(acc, point.timestamp.toMillis()), points[0]?.timestamp.toMillis() ?? NaN)
}

function resolveBounds({ points, granularity, timeZone, from, to, now }: TimelineCsvInput) {
  const rangeStart = from?.isValid ? from : DateTime.fromMillis(extreme(points, Math.min))
  const rangeEnd = to?.isValid ? to : DateTime.fromMillis(extreme(points, Math.max))

  if (!rangeStart.isValid || !rangeEnd.isValid) {
    return undefined
  }

  const start = rangeStart.setZone(timeZone).startOf(granularity)
  const uncappedEnd = rangeEnd.setZone(timeZone)
  // The page clock is set once, so data refetched since then can be newer; never cut that off.
  const latestPoint = DateTime.fromMillis(extreme(points, Math.max))
  const cap = now != null && latestPoint.isValid && latestPoint > now ? latestPoint : now
  const end = (cap != null && cap < uncappedEnd ? cap.setZone(timeZone) : uncappedEnd).startOf(granularity)

  return end < start ? undefined : { start, end }
}

export function buildTimelineCsvRows(input: TimelineCsvInput): CsvCell[][] {
  const { columns, points, granularity, timeZone } = input
  const header: CsvCell[] = ['bucket_start', ...columns]

  const bounds = resolveBounds(input)
  if (bounds == null) {
    return [header]
  }

  const pointsByBucket = new Map(
    points.map((point) => [point.timestamp.setZone(timeZone).startOf(granularity).toMillis(), point])
  )

  const rows: CsvCell[][] = [header]

  for (
    let bucket = bounds.start;
    bucket <= bounds.end && rows.length <= MAX_BUCKETS;
    bucket = bucket.plus({ [granularity]: 1 })
  ) {
    const point = pointsByBucket.get(bucket.toMillis())

    rows.push([bucket.toISO({ suppressMilliseconds: true }), ...columns.map((column) => point?.values[column] ?? 0)])
  }

  return rows
}
